import React from 'react';

import { Check, ChevronLeft } from 'lucide-react';

import type { PricingConfiguration } from '../../../types';

interface MonthlyAndBatchFeesStepProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
	onSave: () => void;
	onPrevious: () => void;
	isLoading: boolean;
	isEditing?: boolean;
	hideNavigation?: boolean;
}

const MonthlyAndBatchFeesStep: React.FC<MonthlyAndBatchFeesStepProps> = ({
	configuration,
	onUpdate,
	onSave,
	onPrevious,
	isLoading,
	isEditing = false,
	hideNavigation = false,
}) => {
	const monthlyFees = configuration.monthly_fees || [];
	const batchFee = configuration.batch_fee || 0;
	const batchCount = configuration.batch_count_per_month || 30;

	const handleAddMonthlyFee = () => {
		onUpdate({
			monthly_fees: [...monthlyFees, { label: '', amount: 0 }],
		});
	};

	const handleRemoveMonthlyFee = (index: number) => {
		onUpdate({
			monthly_fees: monthlyFees.filter((_, i) => i !== index),
		});
	};

	const handleUpdateMonthlyFee = (
		index: number,
		field: 'label' | 'amount',
		value: string | number,
	) => {
		const updated = [...monthlyFees];
		updated[index] = { ...updated[index], [field]: value };
		onUpdate({ monthly_fees: updated });
	};

	// Convert string amounts to numbers for calculation, handling trailing decimals
	const totalMonthlyFees = monthlyFees.reduce((sum, fee) => {
		const amount =
			typeof fee.amount === 'string'
				? parseFloat(fee.amount) || 0
				: fee.amount || 0;
		return sum + amount;
	}, 0);
	const batchFeeNum =
		typeof batchFee === 'string' ? parseFloat(batchFee) || 0 : batchFee || 0;
	const totalBatchFees = batchFeeNum * batchCount;
	const equipmentFeeNum = Number(configuration.equipment_fee) || 0;

	const moneyInput = (
		field: 'gateway_fee' | 'equipment_fee',
		label: string,
		suffix: string,
	) => (
		<div className="flex items-center gap-3">
			<label htmlFor={field} className="text-sm font-medium text-gray-700 w-32">
				{label}:
			</label>
			<div className="flex items-center gap-2">
				<span className="text-gray-600">$</span>
				<input
					id={field}
					type="text"
					inputMode="decimal"
					placeholder="0.00"
					value={configuration[field] ?? ''}
					onChange={(e) => {
						const value = e.target.value;
						if (/^(\d*\.?\d{0,4})?$/.test(value)) {
							// Typed string is kept so "0." survives; the transformer parses it.
							onUpdate({
								[field]: value === '' ? 0 : value,
							} as Partial<PricingConfiguration>);
						}
					}}
					className="w-28 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
				/>
			</div>
			<span className="text-sm text-gray-600">{suffix}</span>
		</div>
	);

	return (
		<div className="space-y-8">
			<div className="text-center mb-8">
				<h3 className="text-2xl text-gray-900 mb-2">Monthly & Batch Fees</h3>
				<p className="text-gray-600">
					Configure monthly recurring fees and batch settlement fees for your
					proposals
				</p>
			</div>

			{/* Monthly Fees Section */}
			<div className="space-y-4">
				<div>
					<h4 className="text-lg font-medium text-gray-900 mb-2">
						Monthly Fees
					</h4>
					<p className="text-sm text-gray-600 mb-4">
						Add monthly recurring fees (e.g., PCI Compliance, Gateway Fee,
						Statement Fee)
					</p>
				</div>

				<div className="space-y-3">
					{monthlyFees.map((fee, index) => (
						<div
							key={index}
							className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg"
						>
							<input
								type="text"
								placeholder="Label (e.g., PCI Fee)"
								value={fee.label}
								onChange={(e) =>
									handleUpdateMonthlyFee(index, 'label', e.target.value)
								}
								className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
							/>
							<div className="flex items-center gap-2">
								<span className="text-gray-600">$</span>
								<input
									type="text"
									inputMode="decimal"
									placeholder="0.00"
									value={
										fee.amount !== undefined && fee.amount !== null
											? fee.amount
											: ''
									}
									onChange={(e) => {
										const value = e.target.value;
										// Allow empty, numbers, and numbers with up to 2 decimal places
										if (/^(\d*\.?\d{0,2})?$/.test(value)) {
											// Store the value as string to preserve trailing decimal/zeros during typing
											handleUpdateMonthlyFee(
												index,
												'amount',
												value === '' ? 0 : value,
											);
										}
									}}
									className="w-28 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
								/>
							</div>
							<button
								type="button"
								onClick={() => handleRemoveMonthlyFee(index)}
								className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium"
							>
								Remove
							</button>
						</div>
					))}
				</div>

				<button
					type="button"
					onClick={handleAddMonthlyFee}
					className="w-full px-4 py-3 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 font-medium flex items-center justify-center gap-2"
				>
					<span className="text-xl">+</span>
					<span>Add Monthly Fee</span>
				</button>

				{monthlyFees.length > 0 && (
					<div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
						<p className="text-sm text-blue-900">
							<span className="font-semibold">Total Monthly Fees:</span> $
							{totalMonthlyFees.toFixed(2)}/month
						</p>
					</div>
				)}
			</div>

			{/* Batch Fee Section */}
			<div className="space-y-4">
				<div>
					<h4 className="text-lg font-medium text-gray-900 mb-2">Batch Fee</h4>
					<p className="text-sm text-gray-600 mb-4">
						Configure per-batch settlement fees
					</p>
				</div>

				<div className="p-4 bg-gray-50 rounded-lg space-y-4">
					<div className="flex items-center gap-3">
						<label className="text-sm font-medium text-gray-700 w-32">
							Per-Batch Fee:
						</label>
						<div className="flex items-center gap-2">
							<span className="text-gray-600">$</span>
							<input
								type="text"
								inputMode="decimal"
								placeholder="0.00"
								value={
									batchFee !== undefined && batchFee !== null ? batchFee : ''
								}
								onChange={(e) => {
									const value = e.target.value;
									// Allow empty, numbers, and numbers with up to 2 decimal places
									if (/^(\d*\.?\d{0,2})?$/.test(value)) {
										// Store the value as string to preserve trailing decimal/zeros during typing
										onUpdate({ batch_fee: value === '' ? 0 : value });
									}
								}}
								className="w-28 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
							/>
						</div>
					</div>

					<div className="flex items-center gap-3">
						<label className="text-sm font-medium text-gray-700 w-32">
							Batches/Month:
						</label>
						<input
							type="number"
							min="1"
							max="365"
							value={batchCount}
							onChange={(e) =>
								onUpdate({
									batch_count_per_month: parseInt(e.target.value) || 30,
								})
							}
							className="w-28 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
						/>
						<span className="text-sm text-gray-600">batches per month</span>
					</div>

					{totalBatchFees > 0 && (
						<div className="pt-3 border-t border-gray-200">
							<p className="text-sm text-gray-700">
								<span className="font-semibold">Total Batch Fees:</span> $
								{totalBatchFees.toFixed(2)}/month
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Gateway & Equipment Fees */}
			<div className="space-y-4">
				<div>
					<h4 className="text-lg font-medium text-gray-900 mb-2">
						Gateway & Equipment Fees
					</h4>
					<p className="text-sm text-gray-600 mb-4">
						The gateway fee is charged on every transaction. A monthly gateway
						fee goes under Monthly Fees.
					</p>
				</div>
				<div className="p-4 bg-gray-50 rounded-lg space-y-4">
					{moneyInput('gateway_fee', 'Gateway Fee', 'per transaction')}
					{moneyInput('equipment_fee', 'Equipment Fee', 'per month')}
				</div>
			</div>

			{/* Grand Total Summary */}
			{(totalMonthlyFees > 0 || totalBatchFees > 0 || equipmentFeeNum > 0) && (
				<div className="p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
					<p className="text-lg font-semibold text-purple-900">
						Grand Total: $
						{(totalMonthlyFees + totalBatchFees + equipmentFeeNum).toFixed(2)}
						/month
						{Number(configuration.gateway_fee) > 0 &&
							' + gateway per transaction'}
					</p>
				</div>
			)}

			{/* Navigation */}
			{!hideNavigation && (
				<div className="flex justify-between pt-6">
					<button
						onClick={onPrevious}
						className="flex items-center gap-2 px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
					>
						<ChevronLeft className="w-5 h-5" />
						Previous
					</button>
					<button
						onClick={onSave}
						disabled={isLoading}
						className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
					>
						<Check className="w-5 h-5" />
						{isEditing
							? isLoading
								? 'Updating...'
								: 'Update Configuration'
							: isLoading
								? 'Creating...'
								: 'Create Configuration'}
					</button>
				</div>
			)}
		</div>
	);
};

export default MonthlyAndBatchFeesStep;
